import { Component, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, Subscription } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { DataExchangeRequestApiService } from 'src/app/data-transfer/services/data-exchange-request-api-service';
import { DataExchangeRequestDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-request-dto';
import { DataExchangeRequestStatusDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-request-status-dto';
import { PaginationDataSource } from 'src/app/util/pagination-datasource';
import { DataExchangeRequestPaginationFields } from 'src/app/data-transfer/entities/pagination-fields';

@Component({
  selector: 'app-data-exchange-requests',
  templateUrl: './data-exchange-requests.component.html',
  styleUrls: ['./data-exchange-requests.component.scss'],
})
export class DataExchangeRequestsComponent implements OnInit, OnDestroy {
  readonly displayedColumns: string[] = ['expand', 'id', 'name', 'counterpart', 'subRequestCount', 'createdAt', 'status', 'actions'];

  selectedTabIndex = 0;
  showClosedAndDeleted = false;

  sentDataSource = new PaginationDataSource<DataExchangeRequestDto>(query => this.dataExchangeRequestApiService.getSentRequestsByPage(query, true));
  receivedDataSource = new PaginationDataSource<DataExchangeRequestDto>(query => this.dataExchangeRequestApiService.getReceivedRequestsByPage(query, true));
  closedSentDataSource = new PaginationDataSource<DataExchangeRequestDto>(query => this.dataExchangeRequestApiService.getSentRequestsByPage(query, true));
  closedReceivedDataSource = new PaginationDataSource<DataExchangeRequestDto>(query => this.dataExchangeRequestApiService.getReceivedRequestsByPage(query, true));

  private queryParamsSubscription?: Subscription;
  private deepLinkSubscription?: Subscription;

  requestIdToExpand: number | null = null;

  constructor(
    private dataExchangeRequestApiService: DataExchangeRequestApiService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.queryParamsSubscription = this.route.queryParams.subscribe(params => {
      this.selectedTabIndex = +(params['tab'] ?? 0);
      this.requestIdToExpand = params['requestId'] ? +params['requestId'] : null;
      if (this.requestIdToExpand != null) {
        this.resolveDeepLinkedRequestTab(this.requestIdToExpand);
      }
    });
  }

  loadData(): void {
    this.sentDataSource.refresh();
    this.receivedDataSource.refresh();
    if (this.showClosedAndDeleted) {
      this.closedSentDataSource.refresh();
      this.closedReceivedDataSource.refresh();
    }
  }

  private resolveDeepLinkedRequestTab(requestId: number): void {
    this.deepLinkSubscription?.unsubscribe();
    const idFilter = { pageIndex: 0, pageSize: 1, sortAscending: true, sortByField: undefined, filters: [{ fieldName: DataExchangeRequestPaginationFields.Id, values: [requestId.toString()] }] };
    this.deepLinkSubscription = forkJoin({
      sent: this.dataExchangeRequestApiService.getSentRequestsByPage(idFilter, true),
      received: this.dataExchangeRequestApiService.getReceivedRequestsByPage(idFilter, true),
    }).subscribe(({ sent, received }) => {
      const hit = sent.items[0] ?? received.items[0];
      if (!hit) {
        return;
      }
      this.selectedTabIndex = sent.items[0] ? 0 : 1;
      if (hit.status === DataExchangeRequestStatusDto.Closed || hit.status === DataExchangeRequestStatusDto.Deleted) {
        this.showClosedAndDeleted = true;
      }
    });
  }

  toggleShowClosedAndDeleted(): void {
    this.showClosedAndDeleted = !this.showClosedAndDeleted;
    if (this.showClosedAndDeleted) {
      this.closedSentDataSource.refresh();
      this.closedReceivedDataSource.refresh();
    }
  }

  onTabChange(index: number): void {
    this.selectedTabIndex = index;
    this.router.navigate([], { queryParams: { tab: index }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  ngOnDestroy(): void {
    this.queryParamsSubscription?.unsubscribe();
    this.deepLinkSubscription?.unsubscribe();
  }
}
